/**
 * Linha do tempo do caso de garantia + próximos passos (Task 15). Mesma
 * divisão de responsabilidade do `FulfillmentStateMachine`:
 *
 * 1. **Mostrar o histórico**: cada passo registrado (`rma_case_events`,
 *    migration 0034) com etapa de origem/destino, QUEM registrou, quando, o
 *    responsável/prazo da etapa, as evidências e a comunicação enviada ao
 *    cliente. Passo espelhado do sistema aparece como `system`.
 * 2. **Oferecer o que a máquina permite**: as etapas e as provas exigidas vêm
 *    de `nextRmaStates`/`rmaRequirementsFor` (regra pura), não de uma lista de
 *    botões mantida aqui. Etapa final não oferece avanço.
 *
 * O caminho de gravação entra por prop (`transition`): sem ele a tela é
 * leitura e DIZ isso, em vez de botões que não gravam.
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * apenas (`createElement` → JSX): o ícone `Circle` da timeline já existia
 * antes desta migração (achado da investigação inicial do cluster) — nenhum
 * ícone novo foi adicionado. Nenhum `data-*`/aria/texto/contrato de submit
 * muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { Circle } from "lucide-react";
import { StatusPanel } from "./StatusPanel.js";
import {
  RMA_STATE_LABELS,
  RMA_RESOLUTIONS,
  RMA_RESOLUTION_LABELS,
  RMA_FIELD_LABELS,
  checkRmaTransition,
  isTerminalRmaState,
  rmaRequirementsFor,
  resolutionNeedsFinancialReference,
  isRmaResolution,
  type RmaState,
  type RmaResolution,
  type RmaTransitionCheck,
  type RmaTransitionField,
  type RmaTransitionInput
} from "../rma/state-machine.js";
import { availableRmaTransitions, rmaTimeline } from "../rma/dataset.js";
import { formatDateTime } from "../format.js";
import type { RmaTransitionHandle } from "../rma/view-props.js";
import type { PlatformRmaCase } from "../api/types.js";

export interface RmaStateMachineProps {
  rmaCase: PlatformRmaCase;
  transition?: RmaTransitionHandle;
}

export interface RmaFormValues {
  get(name: string): unknown;
}

export type RmaSubmission =
  | { ok: true; input: { to: RmaState } & RmaTransitionInput }
  | { ok: false; check: RmaTransitionCheck };

/**
 * Caminho de submit REAL do avanço de etapa, isolado do evento de DOM. A
 * validação usa a MESMA `checkRmaTransition` do servidor: não substitui a
 * checagem server-side (a autoridade é o servidor), evita mandar transição já
 * inválida e diz ao operador qual prova falta.
 */
export function submitRmaTransition(
  rmaCase: PlatformRmaCase,
  to: RmaState,
  values: RmaFormValues,
  transition: RmaTransitionHandle
): RmaSubmission {
  const input: RmaTransitionInput = {};
  for (const field of rmaRequirementsFor(to)) {
    const raw = values.get(field);
    if (typeof raw === "string") {
      if (field === "resolution") {
        input.resolution = isRmaResolution(raw) ? raw : (raw as RmaResolution);
      } else {
        input[field] = raw.trim();
      }
    }
  }
  // A referência financeira não é "exigida" por `rmaRequirementsFor`, mas vira
  // obrigatória quando o resultado é reembolso/troca — leia-a do formulário
  // para a validação pura poder cobrá-la (ou dispensá-la).
  if (to === "resolution") {
    const ref = values.get("financialReference");
    if (typeof ref === "string" && ref.trim()) input.financialReference = ref.trim();
  }
  const check = checkRmaTransition(rmaCase.state, to, input);
  if (!check.ok) return { ok: false, check };
  const payload = { to, ...input };
  transition.submit(payload);
  return { ok: true, input: payload };
}

// Redesign R6 (FEAT-005, apresentação apenas): mesmo padrão do
// OrderStateMachine/FulfillmentStateMachine — timeline em cartão tokenizado e
// os formulários de avanço com rótulo associado (htmlFor/id), campos
// tokenizados e CTA primário vermelho único (bg-brand). PRESERVA o contrato de
// submit (submitRmaTransition), todos os data-*/aria e os vocabulários
// RMA_STATE_LABELS/RMA_RESOLUTION_LABELS.
const FIELD_INPUT_CLASS =
  "rounded-control border border-border bg-surface px-3 py-2 text-sm text-ink " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60";
const PRIMARY_BUTTON_CLASS =
  "inline-flex w-fit items-center justify-center rounded-control bg-brand px-4 py-2 text-sm font-semibold text-on-brand " +
  "hover:bg-brand-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60";

function fieldInput(to: RmaState, field: RmaTransitionField, disabled: boolean): ReactElement {
  const id = `rma-${to}-${field}`;
  if (field === "resolution") {
    return (
      <p key={field} className="rma-transition__field flex flex-col gap-1">
        <label htmlFor={id} className="text-sm font-medium text-body">
          {RMA_FIELD_LABELS[field]}
        </label>
        <select id={id} name={field} required disabled={disabled} data-rma-field={field} className={FIELD_INPUT_CLASS}>
          {RMA_RESOLUTIONS.map((resolution) => (
            <option key={resolution} value={resolution}>
              {RMA_RESOLUTION_LABELS[resolution]}
            </option>
          ))}
        </select>
      </p>
    );
  }
  return (
    <p key={field} className="rma-transition__field flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-body">
        {RMA_FIELD_LABELS[field]}
      </label>
      <input id={id} name={field} type="text" required disabled={disabled} data-rma-field={field} className={FIELD_INPUT_CLASS} />
    </p>
  );
}

function StateForm({
  rmaCase,
  to,
  transition
}: {
  rmaCase: PlatformRmaCase;
  to: RmaState;
  transition: RmaTransitionHandle;
}): ReactElement {
  const required = rmaRequirementsFor(to);
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submitRmaTransition(rmaCase, to, new FormData(event.currentTarget), transition);
  }
  // A resolução reembolso/troca exige a referência financeira: a tela oferece
  // o campo e a regra pura decide se é obrigatório (reembolso/troca) ou não.
  const showFinancialRef = to === "resolution";
  return (
    <form
      className="rma-transition flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-rma-to={to}
      onSubmit={onSubmit}
    >
      <h4 className="text-base font-extrabold text-ink">{`Avançar para: ${RMA_STATE_LABELS[to]}`}</h4>
      {required.length || showFinancialRef ? (
        <div className="rma-transition__requirements flex flex-col gap-3" data-rma-requires={required.join(",")}>
          {required.map((field) => fieldInput(to, field, transition.pending))}
          {showFinancialRef ? (
            <p key="financialReference" className="rma-transition__field flex flex-col gap-1">
              <label htmlFor={`rma-${to}-financialReference`} className="text-sm font-medium text-body">
                {`${RMA_FIELD_LABELS.financialReference} (reembolso/troca)`}
              </label>
              <input
                id={`rma-${to}-financialReference`}
                name="financialReference"
                type="text"
                disabled={transition.pending}
                data-rma-field="financialReference"
                className={FIELD_INPUT_CLASS}
              />
            </p>
          ) : null}
        </div>
      ) : (
        <p className="rma-transition__requirements text-sm text-muted" data-rma-requires="">
          Nenhuma prova adicional exigida.
        </p>
      )}
      <button type="submit" disabled={transition.pending} data-action={`rma-${to}`} className={PRIMARY_BUTTON_CLASS}>
        {transition.pending ? "Registrando…" : `Registrar ${RMA_STATE_LABELS[to].toLowerCase()}`}
      </button>
    </form>
  );
}

const COMMUNICATION_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp (WAPI da TDZAIN)",
  email: "E-mail",
  phone: "Telefone",
  none: "Sem comunicação ao cliente"
};

/** Dado que justificou o passo, exibido em texto (nunca escondido). */
function payloadSummary(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!entries.length) return "sem dado adicional";
  return entries
    .map(([key, value]) => {
      if (key === "resolution" && isRmaResolution(value)) return `resolução: ${RMA_RESOLUTION_LABELS[value]}`;
      const fieldLabel = RMA_FIELD_LABELS[key as RmaTransitionField];
      return `${fieldLabel ?? key}: ${String(value)}`;
    })
    .join(" · ");
}

export function RmaStateMachine({ rmaCase, transition }: RmaStateMachineProps): ReactElement {
  const timeline = rmaTimeline(rmaCase);
  const transitions = availableRmaTransitions(rmaCase);
  return (
    <section
      className="rma-state-machine flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-section="rma-state-machine"
    >
      <h3 className="text-lg font-extrabold text-ink">Histórico de etapas</h3>
      <p className="page__note text-sm text-muted" data-rma-state={rmaCase.state} data-rma-events={timeline.length}>
        {`Etapa atual: ${RMA_STATE_LABELS[rmaCase.state]}.`}
      </p>
      {timeline.length ? (
        <ol className="rma-timeline m-0 flex list-none flex-col p-0">
          {timeline.map((event, index) => (
            <li
              key={event.id}
              className="rma-timeline__item relative flex gap-3 pb-4 last:pb-0"
              data-record-id={event.id}
              data-rma-event-to={event.toState}
              data-rma-event-actor-kind={event.actorKind}
            >
              <span className="relative flex w-4 shrink-0 flex-col items-center" aria-hidden>
                <Circle className="mt-0.5 h-3 w-3 shrink-0 fill-brand text-brand" />
                {index < timeline.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
              </span>
              <p className="rma-timeline__content m-0 flex-1 rounded-control bg-surface-soft p-3 text-sm text-body">
                <span className="rma-timeline__states">
                  {event.fromState
                    ? `${RMA_STATE_LABELS[event.fromState]} → ${RMA_STATE_LABELS[event.toState]}`
                    : `Caso aberto em ${RMA_STATE_LABELS[event.toState]}`}
                </span>
                {" · "}
                <span className="rma-timeline__actor">
                  {event.actorKind === "staff" ? `registrado por ${event.actor}` : `espelhado do sistema (${event.actor})`}
                </span>
                {event.assignedTo ? <span className="rma-timeline__assignee">{` · responsável: ${event.assignedTo}`}</span> : null}
                {event.dueAt ? <span className="rma-timeline__due">{` · prazo: ${formatDateTime(event.dueAt)}`}</span> : null}
                {" · "}
                <time dateTime={event.occurredAt}>{formatDateTime(event.occurredAt)}</time>
                {event.reason ? <span className="rma-timeline__reason">{` · motivo: ${event.reason}`}</span> : null}
                {event.evidenceUrls.length ? (
                  <span className="rma-timeline__evidence" data-rma-evidence-count={event.evidenceUrls.length}>
                    {` · evidências: ${event.evidenceUrls.length}`}
                  </span>
                ) : null}
                {event.communicationChannel ? (
                  <span className="rma-timeline__communication" data-rma-communication={event.communicationChannel}>
                    {` · comunicação: ${COMMUNICATION_LABELS[event.communicationChannel] ?? event.communicationChannel}${
                      event.communicationSummary ? ` (${event.communicationSummary})` : ""
                    }`}
                  </span>
                ) : null}
                <span className="rma-timeline__payload">{` · ${payloadSummary(event.payload)}`}</span>
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <StatusPanel
          kind="empty"
          title="Nenhum passo registrado neste caso"
          detail="O caso existe, mas ainda não tem evento na linha do tempo — nada foi inferido no lugar do histórico."
        />
      )}
      <h3 className="text-lg font-extrabold text-ink">Próximos passos</h3>
      {isTerminalRmaState(rmaCase.state) ? (
        <StatusPanel
          kind="empty"
          title={`Etapa final: ${RMA_STATE_LABELS[rmaCase.state]}`}
          detail="Nenhuma etapa sai desta. Um novo defeito abre o seu próprio caso, não reabre este."
        />
      ) : transition ? (
        <div className="rma-state-machine__transitions grid grid-cols-1 gap-4 md:grid-cols-2">
          {transitions.map((option) => (
            <StateForm key={option.to} rmaCase={rmaCase} to={option.to} transition={transition} />
          ))}
        </div>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Avanço de etapa não montado nesta visualização"
          detail={`Desta etapa o caso pode seguir para: ${transitions.map((option) => RMA_STATE_LABELS[option.to]).join(", ")}. Esta montagem é só leitura.`}
        />
      )}
      {transition?.error ? (
        <StatusPanel kind="error" title="O avanço de etapa não foi registrado" detail={transition.error.message} />
      ) : null}
      {transition?.savedState ? <StatusPanel kind="empty" title={`Etapa registrada: ${RMA_STATE_LABELS[transition.savedState]}`} /> : null}
      {/* Lembrete explícito de que a resolução financeira não é executada aqui. */}
      {rmaCase.resolution && resolutionNeedsFinancialReference(rmaCase.resolution) ? (
        <p className="page__note text-sm text-muted" data-rma-financial-reference={rmaCase.financialReference ?? ""}>
          {`Resolução ${RMA_RESOLUTION_LABELS[rmaCase.resolution]} gerou a referência ${
            rmaCase.financialReference ?? "(pendente)"
          } para o Financeiro (Task 16). O RMA não executa o pagamento.`}
        </p>
      ) : null}
    </section>
  );
}
